/**
 * Directional input — the WRITE layer for daily directional stations.
 *
 * These endpoints are INJECTED into the shared `pApi` (so the base contract
 * files `src/api/pApi.ts` / `src/api/types.ts` stay untouched and branches
 * merge cleanly). They are daily_report-scoped and DRILLING-ONLY: Oxbo-P
 * returns 422 for a non-drilling event. TVD / DLS are NOT sent — they are
 * min-curvature-computed downstream and served back read-only.
 *
 * The read side is the existing well-scoped GET /wells/{uwi}/surveys
 * (`useGetWellSurveysQuery`). Because that query declares no cache tag we
 * cannot invalidate it by tag without editing the shared file, so after a
 * successful write we force-refetch it via `getWellSurveys.initiate` in
 * `onQueryStarted` — the Perfilajes screen then reflects the new station.
 */
import { pApi } from "@api/pApi";

/**
 * A daily directional station as returned by the Oxbo-P WRITE routes. This is
 * the write-contract shape (carries `entity_id` / `version` / `source`), which
 * is a superset of the read-side `SurveyStationOut` in `@api/types` — we keep
 * it LOCAL here so the shared types file is not modified.
 */
export interface DailySurveyStationOut {
  id: string;
  entity_id: string;
  version: number;
  event_id: string | null;
  well_uwi: string;
  wellbore_uwi: string | null;
  sequence_no: number;
  md: number;
  inclination: number | null;
  azimuth: number | null;
  tvd: number | null;
  dogleg_severity: number | null;
  survey_date: string | null;
  station_type: string | null;
  /** "daily" for hand-entered daily stations; "definitive" for the pass. */
  source: string | null;
}

/** POST body — `md` is required; TVD/DLS are never accepted. */
export interface DirectionalStationCreate {
  md: number;
  inclination?: number | null;
  azimuth?: number | null;
  sequence_no?: number | null;
  survey_date?: string | null;
  wellbore_uwi?: string | null;
  change_reason?: string | null;
}

/** PUT body — every field optional; keyed on `entity_id` in the URL. */
export type DirectionalStationUpdate = Partial<DirectionalStationCreate>;

export const directionalApi = pApi.injectEndpoints({
  endpoints: (b) => ({
    // POST /reports/{report_id}/directional — add a daily station (drilling only).
    createDirectionalStation: b.mutation<
      DailySurveyStationOut,
      { reportId: string; wellUwi: string; body: DirectionalStationCreate }
    >({
      query: ({ reportId, body }) => ({
        url: `/reports/${reportId}/directional`,
        method: "POST",
        body,
      }),
      async onQueryStarted({ wellUwi }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Refetch the well surveys list so Perfilajes shows the new station.
          if (wellUwi) {
            dispatch(
              pApi.endpoints.getWellSurveys.initiate(wellUwi, {
                subscribe: false,
                forceRefetch: true,
              }),
            );
          }
        } catch {
          // Surfaced to the caller via the mutation's error state.
        }
      },
    }),

    // PUT /reports/{report_id}/directional/{entity_id} — edit a daily station.
    updateDirectionalStation: b.mutation<
      DailySurveyStationOut,
      { reportId: string; entityId: string; wellUwi: string; body: DirectionalStationUpdate }
    >({
      query: ({ reportId, entityId, body }) => ({
        url: `/reports/${reportId}/directional/${entityId}`,
        method: "PUT",
        body,
      }),
      async onQueryStarted({ wellUwi }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          if (wellUwi) {
            dispatch(
              pApi.endpoints.getWellSurveys.initiate(wellUwi, {
                subscribe: false,
                forceRefetch: true,
              }),
            );
          }
        } catch {
          // Surfaced to the caller via the mutation's error state.
        }
      },
    }),
  }),
});

export const { useCreateDirectionalStationMutation, useUpdateDirectionalStationMutation } =
  directionalApi;
